import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Newspaper } from 'lucide-react';
import { z } from 'zod';
import { useUpdates } from '@client/lib/investor-api';
import { UpdateCard } from '@client/components/investor/update-card';
import { EmptyState, Skeleton } from '@client/components/ui/feedback';
import { cn } from '@client/lib/utils';

export const Route = createFileRoute('/_investor/updates')({
  validateSearch: z.object({ room: z.string().optional(), scope: z.enum(['all']).optional() }),
  component: UpdatesPage,
});

const chip = (active: boolean) => cn('rounded-btn px-3.5 py-1.5 text-[13px] font-[600] no-underline transition-colors', active ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'bg-card text-body shadow-control hover:bg-row-hover dark:border dark:border-border');

/** Every update the investor can read, across rooms, filterable by room or "all investors". */
function UpdatesPage() {
  const { t } = useTranslation();
  const { room, scope } = Route.useSearch();
  const feed = useUpdates({ room, scope });
  const rooms = feed.data?.rooms ?? [];
  const showFilters = rooms.length > 1 || feed.data?.has_company_wide;
  return (
    <main className="mx-auto max-w-[860px] px-5 py-8 md:px-8 md:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">ResilientX</p>
          <h1 className="page-title">{t('updates.title')}</h1>
          <p className="mt-1 text-[14px] text-muted">{t('updates.subtitle')}</p>
        </div>
        {feed.data && feed.data.unread > 0 ? <span className="rounded-btn bg-good-bg px-3 py-1 text-[12.5px] font-[700] text-good">{t('updates.unread', { count: feed.data.unread })}</span> : null}
      </div>
      {showFilters ? (
        <div className="mt-5 flex flex-wrap gap-1.5" role="tablist" aria-label={t('updates.byRoom')}>
          <Link to="/updates" search={{}} role="tab" aria-selected={!room && !scope} className={chip(!room && !scope)}>{t('updates.allRooms')}</Link>
          {feed.data?.has_company_wide ? <Link to="/updates" search={{ scope: 'all' }} role="tab" aria-selected={scope === 'all'} className={chip(scope === 'all')}>{t('updates.allInvestors')}</Link> : null}
          {rooms.map((r) => <Link key={r.id} to="/updates" search={{ room: r.slug }} role="tab" aria-selected={room === r.slug} className={chip(room === r.slug)}>{r.name}</Link>)}
        </div>
      ) : null}
      {feed.isPending ? (
        <div className="mt-6 space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : feed.data && feed.data.updates.length === 0 ? (
        <EmptyState className="mt-6" icon={Newspaper} title={t('updates.empty')} body={t('updates.emptyBody')} />
      ) : (
        <div className="mt-6 space-y-4">
          {feed.data?.updates.map((u, i) => <UpdateCard key={u.id} u={u} delay={i * 40} />)}
        </div>
      )}
    </main>
  );
}
